import React, { useState, useEffect, useCallback } from 'react';
import '../styles/website-form.css';
import * as emailjs from "@emailjs/browser"; // Make sure your CSS is imported

// Dummy data for services and industries - replace with your actual data source
const serviceOptions = [
    { value: 'website', label: 'Web Design Project', title: 'Website Design & Development', placeholder: 'Tell us about your website vision...' },
    { value: 'seo', label: 'SEO Services', title: 'SEO Services', placeholder: 'What are your SEO goals?' },
    // ... other services
];

const industryOptions = [
    { value: 'tech', label: 'Technology' },
    { value: 'finance', label: 'Finance' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'retail', label: 'Retail' },
    { value: 'education', label: 'Education' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'marketing', label: 'Advertising/Marketing' },
    { value: 'food_beverage', label: 'Food & Beverage' },
    // Add more as needed
];

const ContactFormPopup = ({ isOpen, onClose, selectedServiceId = 'website', postSubmit }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
    });
    const [formErrors, setFormErrors] = useState({});
    const [formTouched, setFormTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', '429'
    const [statusMessage, setStatusMessage] = useState('');
    const [retryCount, setRetryCount] = useState(0);

    // State for industry search/selection
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIndustry, setSelectedIndustry] = useState('');
    const [displayList, setDisplayList] = useState(false);

    // Effect to reset form data when the modal opens/closes or service changes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: '',
                email: '',
                company: '',
                phone: '',
                message: '',
            });
            setFormErrors({});
            setFormTouched({});
            setIsSubmitting(false);
            setSubmitStatus(null);
            setStatusMessage('');
            setRetryCount(0);
            setSearchTerm('');
            setSelectedIndustry('');
            setDisplayList(false);
        }
    }, [isOpen, selectedServiceId]);

    // Close modal on escape key press
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    const validateForm = useCallback(() => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Full Name is required';
        if (!formData.email.trim()) {
            errors.email = 'Email Address is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email Address is invalid';
        }
        if (!formData.company.trim()) errors.company = 'Company Name is required';
        if (!formData.phone.trim()) {
            errors.phone = 'Phone Number is required';
        } else if (!/^\+?[0-9\s-()]{7,20}$/.test(formData.phone)) {
            errors.phone = 'Phone Number is invalid';
        }
        if (!formData.message.trim()) errors.message = 'Message is required';
        return errors;
    }, [formData]);

    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'name':
                return !value.trim() ? "Name is required" : null;
            case 'email':
                if (!value.trim()) return "Email is required";
                if (!/\S+@\S+\.\S+/.test(value)) return "Email address is invalid";
                return null;
            case 'message':
                if (!value.trim()) return "Message is required";
                if (value.trim().length < 10) return "Message must be at least 10 characters";
                return null;
            case 'phone':
                if (value && !/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(value))
                    return "Phone number is invalid";
                return null;
            default:
                return null;
        }
    };

    useEffect(() => {
        setFormErrors(validateForm());
    }, [formData, validateForm]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;

        const fieldErrors = validateField(name, formData[name]);
        if (fieldErrors) {
            setFormErrors(prev => ({
                ...prev,
                [name]: fieldErrors
            }));
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleBlur = useCallback((e) => {
        const { name } = e.target;
        setFormTouched(prev => ({ ...prev, [name]: true }));
    }, []);
    // If the modal is not open, don't render anything
    if (!isOpen) return null;

    const selectedService = serviceOptions.find(service => service.value === selectedServiceId) || serviceOptions[0];

    const filteredIndustries = searchTerm
        ? industryOptions.filter(industry =>
            industry.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : industryOptions;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm();

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            // Mark all fields as touched to show errors
            const touchedFields = {};
            Object.keys(formData).forEach(key => {
                touchedFields[key] = true;
            });
            setFormTouched(touchedFields);
            return;
        }

        setIsSubmitting(true);

        try {

            formData.year = new Date().getFullYear();
            formData.interest = selectedService.title;
             emailjs
                .send(
                    "service_jgnpx7g", // Replace with your EmailJS Service ID
                    "template_1y4vqil", // Replace with your EmailJS Template ID
                    formData,
                    "KpU407PeDxzqolG2W" // Replace with your EmailJS Public Key
                );

            if(selectedService.title === "Website Design & Development"){
                const payload = {
                    "body": {
                        "company_name": formData.company,
                        "description": formData.message,
                        "industry": selectedIndustry,
                        "contact_details": {
                            "email": formData.email,
                            "phone": formData.phone,
                            "name": formData.name,
                        },
                        "social_media": {
                            "facebook": "https://facebook.com",
                            "linkedin": "https://linkedin.com"
                        }
                    }
                }

                const fetchWithTimeout = async (url, options, timeout = 10000) => {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), timeout);

                    try {
                        const response = await fetch(url, {
                            ...options,
                            signal: controller.signal
                        });

                        // The request completed, but we still need to read the body
                        // Create a promise that resolves with the response after processing
                        const responsePromise = (async () => {
                            // For JSON responses
                            // const data = await response.json();
                            // For text responses
                            // const data = await response.text();
                            // Or just return the response object
                            return response;
                        })();

                        // Create a timeout promise
                        const timeoutPromise = new Promise((_, reject) => {
                            setTimeout(() => {
                                clearTimeout(timeoutId); // Clear the original timeout
                                reject(new Error('Response processing timed out'));
                            }, timeout);
                        });

                        // Race between processing the response and the timeout
                        const result = await Promise.race([responsePromise, timeoutPromise]);
                        clearTimeout(timeoutId); // Clear the original timeout
                        return result;

                    } catch (error) {
                        clearTimeout(timeoutId);
                        if (error.name === 'AbortError') {
                            console.log(`Request timed out after ${timeout}ms`);
                        }
                    }
                };

                await fetchWithTimeout(
                    "https://9w5zzu1w0j.execute-api.eu-west-1.amazonaws.com/prod/intellommerce/website/process",
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                        mode: 'no-cors', // Bypass CORS
                    },
                    5000 // 5 second timeout for both the request and response processing
                );
            }

            // Reset form
            setFormData({
                name: '',
                email: '',
                company: '',
                phone: '',
                message: '',
                interest: 'general',
                marketingConsent: false
            });

            setSelectedIndustry('')

            setFormTouched({});
            postSubmit();
        } catch (error) {
            //console.error("Error submitting form:", error);
            // Handle submission error
        } finally {
            setIsSubmitting(false);
        }
    }; // Include submitStatus in dependencies


    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}> {/* Prevent clicks inside from closing */}
                <button className="modal-close-button" onClick={onClose}>&times;</button>
                <form id='data-form' className="website-form" onSubmit={handleSubmit}>
                    <h2><span className="highlight">{selectedService.title}</span></h2>
                    <h3>So Eager To Impress You!</h3>
                    <p>Share your details and let's dive right in...!</p>

                    {/* Status Message Display */}
                    {statusMessage && (
                        <p className={`website-form-status-message ${submitStatus === 'success' ? 'success' : submitStatus === 'error' ? 'error' : submitStatus === '429' ? 'warning' : ''}`}>
                            {statusMessage}
                        </p>
                    )}

                    <div className="website-form-row">
                        <div className="website-form-group">
                            <label htmlFor="name">Full Name *</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={formTouched.name && formErrors.name ? 'error' : ''}
                                disabled={isSubmitting}
                            />
                            {formTouched.name && formErrors.name &&
                                <span className="website-form-error-message">{formErrors.name}</span>}
                        </div>

                        <div className="website-form-group">
                            <label htmlFor="email">Email Address *</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={formTouched.email && formErrors.email ? 'error' : ''}
                                disabled={isSubmitting}
                            />
                            {formTouched.email && formErrors.email &&
                                <span className="error-message">{formErrors.email}</span>}
                        </div>
                    </div>

                    <div className="website-form-row">
                        <div className="website-form-group">
                            <label htmlFor="company">Company Name *</label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                onBlur={handleBlur} // Add onBlur for company field too
                                className={formTouched.company && formErrors.company ? 'error' : ''}
                                disabled={isSubmitting}
                            />
                            {formTouched.company && formErrors.company &&
                                <span className="error-message">{formErrors.company}</span>}
                        </div>

                        <div className="website-form-group">
                            <label htmlFor="phone">Phone Number *</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={formTouched.phone && formErrors.phone ? 'error' : ''}
                                disabled={isSubmitting}
                            />
                            {formTouched.phone && formErrors.phone &&
                                <span className="error-message">{formErrors.phone}</span>}
                        </div>
                    </div>

                    <div className="website-form-group">
                        <label htmlFor="industry-search">Which industry are you in?</label>

                        {/* Search Input */}
                        <input
                            type="text"
                            id="industry-search"
                            name="industry-search"
                            placeholder="Type to search..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setDisplayList(true);
                            }}
                            onFocus={() => setDisplayList(true)}
                            onBlur={() => setTimeout(() => setDisplayList(false), 200)} // Delay to allow click on option
                            className="industry-search-input"
                        />

                        {/* Results Dropdown */}
                        {displayList && (
                            <div className="industry-results">
                                {filteredIndustries.length > 0 ? (
                                    filteredIndustries.map(option => (
                                        <div
                                            key={option.value}
                                            className="industry-option"
                                            onMouseDown={(e) => { // Use onMouseDown to trigger before onBlur
                                                e.preventDefault(); // Prevent input blur before click
                                                setSelectedIndustry(option.value);
                                                setSearchTerm(option.label);
                                                setDisplayList(false);
                                            }}
                                        >
                                            {option.label}
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-results">No matching industries found</div>
                                )}
                            </div>
                        )}

                        {/* Hidden select for form submission - still useful for semantic HTML if needed */}
                        <select
                            name="interest"
                            value={selectedIndustry}
                            onChange={() => {}} // No direct change handler, controlled by search/click
                            style={{ display: 'none' }}
                            hidden={true}
                        >
                            {industryOptions.map(option => (
                                <option key={option.value} value={option.value}/>
                            ))}
                        </select>
                    </div>

                    <div className="website-form-group">
                        <label htmlFor="message">{selectedService.label} *</label>
                        <textarea
                            id="message"
                            name="message"
                            rows="5"
                            value={formData.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder={selectedService.placeholder}
                            className={formTouched.message && formErrors.message ? 'error' : ''}
                            disabled={isSubmitting}
                        ></textarea>
                        {formTouched.message && formErrors.message &&
                            <span className="error-message">{formErrors.message}</span>}
                    </div>

                    <div className="hero-cta">
                        <button
                            type="submit"
                            className={`primary-button submit-button ${isSubmitting ? 'submitting' : ''}`}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner"></span>
                                    Sending...
                                </>
                            ) : '🎬 Action Time!'}
                        </button>
                        {/* This button's logic needs to be re-evaluated for a popup context */}
                        <button
                            type="button" // Important: change to type="button" to prevent form submission
                            className="secondary-button"
                            onClick={() => {
                                // This action would typically close the current popup and
                                // potentially open a different form or take the user elsewhere.
                                // For simplicity, let's just close the popup here.
                                onClose();
                                // You might want to pass a prop like onShowMultipleServicesForm
                                // from the parent to trigger another action.
                            }}
                        >
                            I want more than one service →
                        </button>
                    </div>

                    <p className="website-form-disclaimer">
                        By submitting this form, you agree to our <a href="?modal=privacy">Privacy Policy</a> and <a
                        href="?modal=privacy">Terms of Service</a>.
                    </p>
                </form>
            </div>
        </div>
    );
}

export default ContactFormPopup;